import { Fragment, useState } from 'react';
import { usePerformance, useTeams } from '../api/hooks';
import type { PerformanceRow, RangePreset, SourceRow, TeamPerformanceRow } from '../api/types';
import { useAuth } from '../auth/AuthContext';
import { StatTile } from '../components/charts';
import { ErrorBanner } from '../components/ui';

/**
 * How each person is doing with the leads they were given.
 *
 * Everything is anchored to when a lead was *assigned*, not when it arrived:
 * the question is what someone did with their own workload, so a lead handed
 * over in August is August's work whenever it was created. Status is read as
 * it stands today — "of the 100 you got in July, where are they now?"
 */

const PRESETS: Array<{ key: RangePreset; label: string }> = [
  { key: 'today', label: 'Today' },
  { key: 'yesterday', label: 'Yesterday' },
  { key: 'last_7', label: '7 days' },
  { key: 'last_30', label: '30 days' },
  { key: 'this_month', label: 'This month' },
  { key: 'last_month', label: 'Last month' },
  { key: 'all', label: 'All time' },
];

type SortKey = 'assigned' | 'converted' | 'conversion_rate' | 'contact_rate' | 'dnp_rate';

export function TeamPerformancePage() {
  const { user } = useAuth();
  const isAdmin = user?.scope === 'all';
  const { data: teams = [] } = useTeams();

  const [range, setRange] = useState<RangePreset>('last_30');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [teamId, setTeamId] = useState('');
  const [sort, setSort] = useState<SortKey>('assigned');
  const [expanded, setExpanded] = useState<string | null>(null);
  const [expandedSource, setExpandedSource] = useState<string | null>(null);
  const [expandedTeam, setExpandedTeam] = useState<string | null>(null);

  const { data, isPending, isFetching, error } = usePerformance({
    range,
    from: from || undefined,
    to: to || undefined,
    teamId: teamId || undefined,
  });

  const totals = data?.totals;
  const members = [...(data?.members ?? [])].sort((a, b) => b[sort] - a[sort]);
  // Only worth a column when the rows actually come from different teams.
  const showTeamColumn = (data?.teams.length ?? 0) > 0;

  return (
    <div className="page">
      <div className="page-head">
        <div>
          <h1>Team performance</h1>
          <p className="sub">
            {data?.team ? `${data.team.name} · ` : ''}
            leads assigned {data?.range.label.toLowerCase() ?? '…'}, and where they stand today
          </p>
        </div>
      </div>

      <ErrorBanner error={error} />

      {/* ------------------------------ range ------------------------------ */}
      <div className="card" style={{ marginBottom: 16 }}>
        <div className="card-body" style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>
          <div className="seg" role="tablist" aria-label="Date range">
            {[...PRESETS, { key: 'custom' as RangePreset, label: 'Custom' }].map((p) => (
              <button
                key={p.key}
                type="button"
                role="tab"
                aria-selected={range === p.key}
                className={range === p.key ? 'active' : ''}
                onClick={() => setRange(p.key)}
              >
                {p.label}
              </button>
            ))}
          </div>

          {range === 'custom' && (
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              <input
                type="date"
                aria-label="From date"
                value={from}
                max={to || undefined}
                onChange={(e) => setFrom(e.target.value)}
                style={{ width: 165 }}
              />
              <span style={{ color: 'var(--muted)' }}>→</span>
              <input
                type="date"
                aria-label="To date"
                value={to}
                min={from || undefined}
                onChange={(e) => setTo(e.target.value)}
                style={{ width: 165 }}
              />
            </div>
          )}

          {isAdmin && teams.length > 0 && (
            <select
              aria-label="Team"
              value={teamId}
              onChange={(e) => setTeamId(e.target.value)}
              style={{ width: 'auto', marginLeft: 'auto' }}
            >
              <option value="">All teams</option>
              {teams.map((t) => (
                <option key={t.id} value={t.id}>{t.name}</option>
              ))}
            </select>
          )}

          {isFetching && !isPending && <span className="spinner" aria-label="Refreshing" />}
        </div>
      </div>

      {range === 'custom' && !(from && to) ? (
        <div className="card">
          <div className="card-body">
            <p style={{ margin: 0, color: 'var(--muted)' }}>Pick both dates to see the numbers.</p>
          </div>
        </div>
      ) : isPending || !totals ? (
        <div className="skeleton" style={{ height: 320, borderRadius: 14 }} />
      ) : (
        <>
          {/* ------------------------- the funnel ------------------------- */}
          <div className="kpi-row">
            <StatTile label="Leads assigned" value={totals.assigned} hint={data?.range.label} />
            <StatTile
              label="DNP"
              value={totals.dnp}
              tone={totals.dnp_rate > 25 ? 'warning' : 'default'}
              hint={`${totals.dnp_rate}% never picked up`}
            />
            <StatTile label="Invalid" value={totals.invalid} hint="Junk or unusable" />
            <StatTile
              label="Contactable"
              value={totals.contactable}
              hint="Assigned − DNP − invalid"
            />
            <StatTile
              label="Converted"
              value={totals.converted}
              hint={`${totals.conversion_rate}% of contactable`}
            />
          </div>

          <p className="hint" style={{ margin: '10px 2px 18px' }}>
            {totals.assigned} assigned − {totals.dnp} DNP − {totals.invalid} invalid ={' '}
            <strong>{totals.contactable} contactable</strong>, of which {totals.converted} converted
            → <strong>{totals.conversion_rate}%</strong>. Conversion means a lead reached
            “Admitted”; Qualified and Application Started count as pipeline, not conversions.
          </p>

          {totals.assigned === 0 && (
            <div className="card">
              <div className="card-body">
                <p style={{ margin: 0, color: 'var(--muted)' }}>
                  No leads were assigned to this team {data?.range.label.toLowerCase()}. Try a wider
                  range — leads only count here on the day they were handed to someone.
                </p>
              </div>
            </div>
          )}

          {/* --------------------------- trend --------------------------- */}
          {data && data.daily.length > 1 && <TrendChart daily={data.daily} />}

          {/* ---------------------------- teams ---------------------------- */}
          {data && data.teams.length > 0 && (
            <TeamBreakdown
              teams={data.teams}
              expanded={expandedTeam}
              onToggle={(id) => setExpandedTeam(expandedTeam === id ? null : id)}
            />
          )}

          {/* --------------------------- sources --------------------------- */}
          {data && data.sources.length > 0 && (
            <SourceBreakdown
              sources={data.sources}
              teamConversion={totals.conversion_rate}
              expanded={expandedSource}
              onToggle={(id) => setExpandedSource(expandedSource === id ? null : id)}
            />
          )}

          {/* --------------------------- people --------------------------- */}
          {totals.assigned > 0 && (
            <div className="card" style={{ marginTop: 16 }}>
              <div className="card-head">
                <h2>By person</h2>
                <div className="card-actions">
                  <select
                    aria-label="Sort by"
                    value={sort}
                    onChange={(e) => setSort(e.target.value as SortKey)}
                    style={{ width: 'auto' }}
                  >
                    <option value="assigned">Sort: leads assigned</option>
                    <option value="converted">Sort: conversions</option>
                    <option value="conversion_rate">Sort: conversion rate</option>
                    <option value="contact_rate">Sort: contact rate</option>
                    <option value="dnp_rate">Sort: DNP rate</option>
                  </select>
                </div>
              </div>
              <div className="card-body" style={{ paddingTop: 4 }}>
                <div className="table-wrap">
                  <table className="data">
                    <thead>
                      <tr>
                        <th>Person</th>
                        {showTeamColumn && <th>Team</th>}
                        <th className="num">Assigned</th>
                        <th className="num">DNP</th>
                        <th className="num">Invalid</th>
                        <th className="num">Contactable</th>
                        <th className="num">Converted</th>
                        <th className="num">Conv. rate</th>
                        <th className="num">Follow-up</th>
                        <th className="num">Worked</th>
                        <th />
                      </tr>
                    </thead>
                    <tbody>
                      {members.map((m) => (
                        <MemberRows
                          key={m.id}
                          row={m}
                          showTeam={showTeamColumn}
                          teamBest={Math.max(...members.map((x) => x.conversion_rate))}
                          open={expanded === m.id}
                          onToggle={() => setExpanded(expanded === m.id ? null : m.id)}
                        />
                      ))}
                      <tr style={{ fontWeight: 650, borderTop: '2px solid var(--rule)' }}>
                        <td>{showTeamColumn ? 'All teams' : 'Team total'}</td>
                        {showTeamColumn && <td />}
                        <td className="num">{totals.assigned}</td>
                        <td className="num">{totals.dnp}</td>
                        <td className="num">{totals.invalid}</td>
                        <td className="num">{totals.contactable}</td>
                        <td className="num">{totals.converted}</td>
                        <td className="num">{totals.conversion_rate}%</td>
                        <td className="num">{totals.follow_up_rate}%</td>
                        <td className="num">{totals.contact_rate}%</td>
                        <td />
                      </tr>
                    </tbody>
                  </table>
                </div>
                <p className="hint" style={{ marginTop: 10, marginBottom: 0 }}>
                  <strong>Conv. rate</strong> and <strong>Follow-up</strong> are shares of
                  contactable, not of assigned — nobody is marked down for being handed dead
                  numbers. <strong>Worked</strong> is the share of contactable leads moved off
                  “New”. Click a row for the full status breakdown.
                </p>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ */

/**
 * Which team is carrying the volume, and which is converting it.
 *
 * Share of assigned and share of conversions are shown side by side on
 * purpose: a team taking 60% of the leads and producing 30% of the admissions
 * is the finding, and neither column says it alone.
 */
function TeamBreakdown({
  teams,
  expanded,
  onToggle,
}: {
  teams: TeamPerformanceRow[];
  expanded: string | null;
  onToggle: (id: string) => void;
}) {
  const totalAssigned = teams.reduce((n, t) => n + t.assigned, 0);
  const totalConverted = teams.reduce((n, t) => n + t.converted, 0);
  const share = (n: number, of: number) => (of > 0 ? Math.round((n / of) * 100) : 0);

  return (
    <div className="card" style={{ marginTop: 16 }}>
      <div className="card-head">
        <h2>By team</h2>
        <div className="card-actions">
          <span className="chip chip-plain">{teams.length} teams</span>
        </div>
      </div>
      <div className="card-body" style={{ paddingTop: 4 }}>
        <div className="table-wrap">
          <table className="data">
            <thead>
              <tr>
                <th>Team</th>
                <th>Manager</th>
                <th className="num">People</th>
                <th className="num">Assigned</th>
                <th className="num">Share</th>
                <th className="num">Contactable</th>
                <th className="num">Converted</th>
                <th className="num">Of all conv.</th>
                <th className="num">Conv. rate</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {teams.map((t) => {
                const open = expanded === t.id;
                const volumeShare = share(t.assigned, totalAssigned);
                const winShare = share(t.converted, totalConverted);
                return (
                  <Fragment key={t.id}>
                    <tr
                      onClick={() => onToggle(t.id)}
                      style={{ cursor: 'pointer', opacity: t.assigned === 0 ? 0.55 : 1 }}
                      aria-expanded={open}
                    >
                      <td><strong>{t.name}</strong></td>
                      <td>{t.manager_name ?? <span style={{ color: 'var(--faint)' }}>—</span>}</td>
                      <td className="num">{t.member_count}</td>
                      <td className="num">{t.assigned}</td>
                      <td className="num">{volumeShare}%</td>
                      <td className="num">{t.contactable}</td>
                      <td className="num">{t.converted}</td>
                      <td
                        className="num"
                        title="Share of every conversion across all teams"
                        style={{
                          color:
                            t.assigned === 0
                              ? 'var(--muted)'
                              : winShare > volumeShare
                                ? 'var(--good)'
                                : winShare < volumeShare
                                  ? 'var(--danger)'
                                  : 'var(--muted)',
                        }}
                      >
                        {winShare}%
                      </td>
                      <td className="num"><strong>{t.conversion_rate}%</strong></td>
                      <td style={{ textAlign: 'right', color: 'var(--muted)' }} aria-hidden="true">
                        {open ? '▾' : '▸'}
                      </td>
                    </tr>
                    {open && (
                      <tr>
                        <td colSpan={10} style={{ background: 'var(--sunken)' }}>
                          {t.by_member.length === 0 ? (
                            <span style={{ color: 'var(--muted)' }}>
                              Nobody on this team was assigned a lead in this range.
                            </span>
                          ) : (
                            <div style={{ display: 'grid', gap: 6, padding: '4px 0' }}>
                              <div style={{ fontWeight: 650, fontSize: 12.5 }}>
                                {t.name} — per person
                              </div>
                              {t.by_member.map((m) => (
                                <div
                                  key={m.id}
                                  style={{ display: 'flex', gap: 12, alignItems: 'center', fontSize: 13 }}
                                >
                                  <span style={{ minWidth: 150 }}>{m.full_name}</span>
                                  <span style={{ minWidth: 90, color: 'var(--muted)' }}>
                                    {m.assigned} assigned
                                  </span>
                                  <span style={{ minWidth: 110, color: 'var(--muted)' }}>
                                    {m.converted} converted
                                  </span>
                                  <strong>{m.conversion_rate}%</strong>
                                </div>
                              ))}
                            </div>
                          )}
                        </td>
                      </tr>
                    )}
                  </Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
        <p className="hint" style={{ marginTop: 10, marginBottom: 0 }}>
          <strong>Share</strong> is this team's cut of all leads assigned; <strong>Of all conv.</strong>{' '}
          is its cut of every conversion. A team whose conversion share is below its volume share is
          being handed more than it is closing. Click a row for the people behind it.
        </p>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */

/**
 * The same funnel, cut by channel.
 *
 * Read alongside the per-person table this answers the question that table
 * raises but cannot settle: whether a low conversion rate is the person or the
 * leads they were handed. Each row compares against the team's own rate rather
 * than an absolute target, because what counts as good varies by intake.
 */
function SourceBreakdown({
  sources,
  teamConversion,
  expanded,
  onToggle,
}: {
  sources: SourceRow[];
  teamConversion: number;
  expanded: string | null;
  onToggle: (id: string) => void;
}) {
  const totalAssigned = sources.reduce((n, s) => n + s.assigned, 0);

  return (
    <div className="card" style={{ marginTop: 16 }}>
      <div className="card-head">
        <h2>By source</h2>
        <div className="card-actions">
          <span className="chip chip-plain">{sources.length} channels</span>
        </div>
      </div>
      <div className="card-body" style={{ paddingTop: 4 }}>
        <div className="table-wrap">
          <table className="data">
            <thead>
              <tr>
                <th>Source</th>
                <th className="num">Assigned</th>
                <th className="num">Share</th>
                <th className="num">DNP</th>
                <th className="num">Invalid</th>
                <th className="num">Contactable</th>
                <th className="num">Converted</th>
                <th className="num">Conv. rate</th>
                <th className="num">vs team</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {sources.map((s) => {
                const diff = Math.round((s.conversion_rate - teamConversion) * 10) / 10;
                const open = expanded === s.id;
                return (
                  <Fragment key={s.id}>
                    <tr onClick={() => onToggle(s.id)} style={{ cursor: 'pointer' }} aria-expanded={open}>
                      <td><strong>{s.name}</strong></td>
                      <td className="num">{s.assigned}</td>
                      <td className="num">
                        {totalAssigned > 0 ? Math.round((s.assigned / totalAssigned) * 100) : 0}%
                      </td>
                      <td className="num">{s.dnp}</td>
                      <td className="num">{s.invalid}</td>
                      <td className="num">{s.contactable}</td>
                      <td className="num">{s.converted}</td>
                      <td className="num"><strong>{s.conversion_rate}%</strong></td>
                      <td
                        className="num"
                        style={{
                          color:
                            Math.abs(diff) < 0.5
                              ? 'var(--muted)'
                              : diff > 0
                                ? 'var(--good)'
                                : 'var(--danger)',
                        }}
                      >
                        {/* Arrow plus number, never colour alone. */}
                        <span aria-hidden="true">{Math.abs(diff) < 0.5 ? '→' : diff > 0 ? '↑' : '↓'}</span>{' '}
                        {Math.abs(diff)}pp
                      </td>
                      <td style={{ textAlign: 'right', color: 'var(--muted)' }} aria-hidden="true">
                        {open ? '▾' : '▸'}
                      </td>
                    </tr>
                    {open && (
                      <tr>
                        <td colSpan={10} style={{ background: 'var(--sunken)' }}>
                          <div style={{ display: 'grid', gap: 6, padding: '4px 0' }}>
                            <div style={{ fontWeight: 650, fontSize: 12.5 }}>
                              Who worked {s.name} leads
                            </div>
                            {s.by_member.map((m) => (
                              <div
                                key={m.id}
                                style={{ display: 'flex', gap: 12, alignItems: 'center', fontSize: 13 }}
                              >
                                <span style={{ minWidth: 150 }}>{m.full_name}</span>
                                <span style={{ minWidth: 90, color: 'var(--muted)' }}>
                                  {m.assigned} assigned
                                </span>
                                <span style={{ minWidth: 110, color: 'var(--muted)' }}>
                                  {m.converted} converted
                                </span>
                                <strong>{m.conversion_rate}%</strong>
                              </div>
                            ))}
                            <div style={{ marginTop: 6 }}>
                              {s.by_status.map((st) => (
                                <span
                                  key={st.key}
                                  style={{ display: 'inline-flex', alignItems: 'center', gap: 6, marginRight: 18 }}
                                >
                                  <span
                                    className="bar-dot"
                                    style={{ background: st.color }}
                                    aria-hidden="true"
                                  />
                                  {st.name} <strong>{st.count}</strong>
                                </span>
                              ))}
                            </div>
                          </div>
                        </td>
                      </tr>
                    )}
                  </Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
        <p className="hint" style={{ marginTop: 10, marginBottom: 0 }}>
          <strong>vs team</strong> is this channel's conversion rate minus the team's overall rate,
          in percentage points — a channel well below it is sending weaker leads, not being worked
          badly. Click a row to see how it landed across the team.
        </p>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */

function MemberRows({
  row,
  showTeam,
  teamBest,
  open,
  onToggle,
}: {
  row: PerformanceRow;
  showTeam: boolean;
  teamBest: number;
  open: boolean;
  onToggle: () => void;
}) {
  const best = teamBest > 0 && row.conversion_rate === teamBest && row.assigned > 0;

  return (
    <>
      <tr
        onClick={onToggle}
        style={{ cursor: 'pointer', opacity: row.is_active ? 1 : 0.55 }}
        aria-expanded={open}
      >
        <td>
          <strong>{row.full_name}</strong>
          {row.is_manager && <span className="tag" style={{ marginLeft: 6 }}>manager</span>}
          {best && (
            <span className="tag" style={{ marginLeft: 6, color: 'var(--good)' }}>
              best rate
            </span>
          )}
        </td>
        {showTeam && (
          <td>
            {row.team_name ?? <span style={{ color: 'var(--faint)' }}>No team</span>}
          </td>
        )}
        <td className="num">{row.assigned}</td>
        <td className="num">{row.dnp}</td>
        <td className="num">{row.invalid}</td>
        <td className="num">{row.contactable}</td>
        <td className="num">{row.converted}</td>
        <td className="num"><strong>{row.conversion_rate}%</strong></td>
        <td className="num">{row.follow_up_rate}%</td>
        <td className="num">{row.contact_rate}%</td>
        <td style={{ textAlign: 'right', color: 'var(--muted)' }} aria-hidden="true">
          {open ? '▾' : '▸'}
        </td>
      </tr>
      {open && (
        <tr>
          <td colSpan={showTeam ? 11 : 10} style={{ background: 'var(--sunken)' }}>
            {row.by_status.length === 0 ? (
              <span style={{ color: 'var(--muted)' }}>No leads assigned in this range.</span>
            ) : (
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px 20px', padding: '4px 0' }}>
                {row.by_status.map((s) => (
                  <span key={s.key} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <span
                      className="bar-dot"
                      style={{ background: s.color, flexShrink: 0 }}
                      aria-hidden="true"
                    />
                    {s.name}
                    <strong>{s.count}</strong>
                    <span style={{ color: 'var(--muted)' }}>
                      ({Math.round((s.count / row.assigned) * 100)}%)
                    </span>
                  </span>
                ))}
              </div>
            )}
          </td>
        </tr>
      )}
    </>
  );
}

/* ------------------------------------------------------------------ */

/**
 * Assignments per day, with conversions overlaid.
 *
 * Bars rather than a line: assignment counts are discrete daily events, and a
 * line between them would imply a continuity that isn't there.
 */
function TrendChart({ daily }: { daily: Array<{ date: string; assigned: number; converted: number }> }) {
  const max = Math.max(1, ...daily.map((d) => d.assigned));
  // Past roughly a quarter's worth of bars the labels collide, so only every
  // Nth is drawn — the tooltip still carries the exact date for all of them.
  const labelEvery = Math.ceil(daily.length / 12);

  return (
    <div className="card" style={{ marginTop: 16 }}>
      <div className="card-head">
        <h2>Assigned per day</h2>
        <div className="card-actions">
          <span className="chip chip-plain">{daily.length} days</span>
        </div>
      </div>
      <div className="card-body">
        <div
          style={{
            display: 'flex',
            alignItems: 'flex-end',
            gap: 2,
            height: 140,
            overflowX: 'auto',
            paddingBottom: 4,
          }}
        >
          {daily.map((d, i) => (
            <div
              key={d.date}
              title={`${d.date} — ${d.assigned} assigned, ${d.converted} converted`}
              style={{
                flex: '1 0 8px',
                minWidth: 8,
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'flex-end',
                alignItems: 'center',
                height: '100%',
              }}
            >
              <div
                style={{
                  width: '100%',
                  height: `${(d.assigned / max) * 100}%`,
                  minHeight: d.assigned > 0 ? 2 : 0,
                  background: 'var(--accent)',
                  borderRadius: '3px 3px 0 0',
                  position: 'relative',
                }}
              >
                {d.converted > 0 && (
                  <div
                    style={{
                      position: 'absolute',
                      bottom: 0,
                      width: '100%',
                      height: `${(d.converted / Math.max(1, d.assigned)) * 100}%`,
                      background: 'var(--good)',
                      borderRadius: '0 0 3px 3px',
                    }}
                  />
                )}
              </div>
              <div
                style={{
                  fontSize: 10,
                  color: 'var(--muted)',
                  marginTop: 4,
                  whiteSpace: 'nowrap',
                  height: 12,
                }}
              >
                {i % labelEvery === 0 ? d.date.slice(5) : ''}
              </div>
            </div>
          ))}
        </div>
        <p className="hint" style={{ marginTop: 8, marginBottom: 0 }}>
          Bar height is leads assigned that day; the green foot is how many of those have since
          converted. Hover any bar for exact numbers.
        </p>
      </div>
    </div>
  );
}
